<template>
  <div style="display: flex; flex-direction: column;">
    <div class="chart buffer">
      <div class="plot" ref="bufferChartContainer"></div>
      <div class="labels" v-if="goalBehind">
        <table>
          <tr>
            <td style="width: 10px; text-align: right"></td>
            <td style="width: 50px;">{{goalBehind}}s &#8676;</td>
            <td style="width: 50px;">&#8677; {{goalAhead}}s</td>
          </tr>
          <tr>
            <td style="text-align: right">&#8721;</td>
            <td>{{bufferTotal}}</td>
            <td>{{bufferTotalSeconds}}</td>
          </tr>
          <tr>
            <td style="text-align: right">&#8677;</td>
            <td>{{bufferAhead}}</td>
            <td>{{bufferAheadSeconds}}</td>
          </tr>
          <tr>
            <td style="text-align: right">&#8676;</td>
            <td>{{bufferBehind}}</td>
            <td>{{bufferBehindSeconds}}</td>
          </tr>
        </table>
      </div>
    </div>
    <div v-if="legend" class="legend">
      <div class="legend-box legend-box-ahead"></div>
      <div class="legend-text legend-space">Front buffer</div>
      <div class="legend-box legend-box-behind"></div>
      <div class="legend-text">Back buffer</div>
    </div>
  </div>
</template>

<script>
import PlayerComponent from '../PlayerComponent.vue';
import { Donut } from '../../../utils/plots';

export default {
  extends: PlayerComponent,
  name: 'PlotCircularBuffer',
  props: {
    legend: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      bufferAhead: '',
      bufferAheadSeconds: '',
      bufferBehind: '',
      bufferBehindSeconds: '',
      bufferTotal: '',
      bufferTotalSeconds: '',
      goalAhead: '',
      goalBehind: '',
      bufferChart: null

    };
  },
  mounted () {
    this.bufferChart = new Donut(this.$refs.bufferChartContainer);
    this.bufferChart.size = 30;
    this.bufferChart.init();
    this.bufferChart.setData([
      { color: '#00586f', value: 0 },
      { color: '#00ddff', value: 0 },
      { color: '#ababab', value: 100 }
    ]);
    let accumulatedDelta = 0;
    this.bufferChart.onUpdate = (lt) => {
      const delta = (new Date().getTime() - lt) / 1000.0;
      accumulatedDelta += delta;
      const data = this.bufferChart.data;
      if (delta > 0 && data.length === 3) {
        data[0].value = data[0].value + delta;
        data[1].value = Math.max(0, data[1].value - delta);
        if (accumulatedDelta > 0.01) {
          this.updateStatsFromPlotData();
          accumulatedDelta = 0;
        }
      }
    };

    this.updateBufferInfo();
    this.onStateChanged();
  },
  methods: {
    initPlayer (ppPlayerRef) {
      ppPlayerRef.get().on('timeupdate', this.updateBufferInfo);
      ppPlayerRef.get().on('videobufferchanged', this.updateBufferInfo);
      ppPlayerRef.get().on('statechanged', this.onStateChanged);
    },
    disposePlayer (ppPlayerRef) {
      ppPlayerRef.get().off('timeupdate', this.updateBufferInfo);
      ppPlayerRef.get().off('videobufferchanged', this.updateBufferInfo);
      ppPlayerRef.get().off('statechanged', this.onStateChanged);
    },

    onStateChanged () {
      if (this.ppPlayerRef && this.ppPlayerRef.get().getState() === clpp.Player.State.PLAYING) {
        this.bufferChart.start();
      } else {
        this.bufferChart.stop();
      }
      this.updateBufferInfo();
    },

    updateBufferInfo () {
      if (!this.ppPlayerRef) return;
      const cfg = this.ppPlayerRef.get().getConfiguration();
      if (!cfg) return;
      if (!cfg.streaming) return;

      this.goalAhead = cfg.streaming.bufferingGoal;
      this.goalBehind = cfg.streaming.bufferBehind;
      const maxBuffer = cfg.streaming.bufferBehind + cfg.streaming.bufferingGoal;
      const buffered = this.ppPlayerRef.get().getBufferInfo();
      let ahead = 0;
      let behind = 0;
      const t = this.ppPlayerRef.get().getPosition();
      for (let i = 0; i < buffered.length; i++) {
        const start = buffered.start(i);
        const end = buffered.end(i);
        const dur = end - start;
        if (end <= t) {
          behind += dur;
        } else if (start >= t) {
          ahead += dur;
        } else {
          behind += dur - (end - t);
          ahead += dur - (t - start);
        }
      }
      if (this.bufferChart && this.bufferChart.data.length === 3) {
        this.bufferChart.data[0].value = behind;
        this.bufferChart.data[1].value = ahead;
        this.bufferChart.data[2].value = Math.max(0, maxBuffer - (behind + ahead));
        this.bufferChart.draw();
        this.updateStatsFromPlotData();
      }
    },

    updateStatsFromPlotData () {
      if (!this.bufferChart && this.bufferChart.data.length !== 3) {
        return;
      }
      const data = this.bufferChart.data;
      const behind = data[0].value;
      const ahead = data[1].value;
      const cfg = this.ppPlayerRef.get().getConfiguration();
      this.goalAhead = cfg.streaming.bufferingGoal;
      this.goalBehind = cfg.streaming.bufferBehind;
      const maxBuffer = cfg.streaming.bufferBehind + cfg.streaming.bufferingGoal;

      const total = behind + ahead;
      const p = Math.min(100, Math.round(total / maxBuffer * 100.0));
      const p_ahead = Math.min(100, Math.round(ahead / maxBuffer * 100.0));
      let p_behind = Math.min(100, Math.round(behind / maxBuffer * 100.0));
      if (p >= 100.0) {
        p_behind = 100.0 - p_ahead;
      }

      this.bufferBehind = p_behind.toFixed(0) + '%';
      this.bufferAhead = p_ahead.toFixed(0) + '%';
      this.bufferTotal = p.toFixed(0) + '%';

      this.bufferAheadSeconds = ahead.toFixed(2);
      this.bufferBehindSeconds = behind.toFixed(2);
      this.bufferTotalSeconds = total.toFixed(2);
    }
  }
};
</script>

<style scoped lang="scss">
  @import 'src/style/color_';

  .chart {
    position: relative;
    height: 220px;
    width: 220px;

    border-radius: 50%;
    margin: auto;
  }

  .chart .labels {
    display: flex;
    justify-content: center;
    flex-direction: column;

    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    text-align: center;

    padding: 0 50px 0;

    color: #666666;
  }

  .plot {
    width: 100%;
    height: 100%;
  }

  .legend {
    text-align: center;
  }

  .legend-text {
    display: inline-block;
    color: $color-secondary;
  }

  .legend-box {
    width: 10px;
    height: 10px;
    margin-right: 8px;
    margin-left: 8px;
    display: inline-block;
  }

  .legend-box-ahead {
    background-color: #00ddff;
  }

  .legend-box-behind {
    background-color: #00586f;
  }
  .legend-space {
    margin-right: 24px;
  }

</style>
